import React, { useEffect, useMemo, useRef, useState } from "react";

import { Check, Columns3, RotateCcw, X } from "lucide-react";

const ColumnVisibilityDropdown = ({
  columns = [],
  visibleColumns = [],
  setVisibleColumns,
  storageKey = "datatable-columns",
}) => {
  const [open, setOpen] = useState(false);

  const dropdownRef = useRef(null);

  // =========================
  // DERIVED STATE
  // =========================

  const hiddenColumnsCount = useMemo(
    () => columns.length - visibleColumns.length,
    [columns.length, visibleColumns.length],
  );

  const hasHiddenColumns = hiddenColumnsCount > 0;

  // =========================
  // LOAD SAVED COLUMNS
  // =========================

  useEffect(() => {
    const savedColumns = localStorage.getItem(storageKey);

    if (savedColumns) {
      setVisibleColumns(JSON.parse(savedColumns));
    }
  }, [storageKey, setVisibleColumns]);

  // =========================
  // SAVE COLUMNS
  // =========================

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(visibleColumns));
  }, [storageKey, visibleColumns]);

  // =========================
  // CLOSE ON OUTSIDE CLICK
  // =========================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // =========================
  // TOGGLE COLUMN
  // =========================

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      // PREVENT HIDING LAST COLUMN
      if (prev.includes(key) && prev.length === 1) {
        return prev;
      }

      return prev.includes(key)
        ? prev.filter((item) => item !== key)
        : [...prev, key];
    });
  };

  // =========================
  // SELECT ALL
  // =========================

  const handleSelectAll = () => {
    setVisibleColumns(columns.map((col) => col.key));
  };

  // =========================
  // RESET
  // =========================

  const handleReset = () => {
    setVisibleColumns(columns.map((col) => col.key));
  };

  return (
    <div ref={dropdownRef} className="relative inline-block">
      {/* BUTTON */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`
          relative flex items-center gap-2
          rounded-xl border px-3 py-2
          transition-all duration-200

          ${
            hasHiddenColumns
              ? `
                border-amber-300 dark:border-amber-700
                bg-amber-50 dark:bg-amber-900/20
                text-amber-700 dark:text-amber-300
                hover:bg-amber-100 dark:hover:bg-amber-900/30
                shadow-sm
              `
              : `
                border-slate-200 dark:border-slate-700
                bg-white dark:bg-slate-900
                text-slate-700 dark:text-slate-200
                hover:bg-slate-50 dark:hover:bg-slate-800
              `
          }
        `}
      >
        <Columns3 size={16} />

        <span className="text-sm font-medium">
          {hasHiddenColumns
            ? `${visibleColumns.length}/${columns.length} Columns`
            : "Columns"}
        </span>

        {hasHiddenColumns && (
          <>
            {/* HIDDEN COUNT */}
            <span
              className="
                inline-flex items-center justify-center
                h-5 min-w-[20px]
                rounded-full
                bg-amber-500
                px-1.5
                text-[10px] font-bold text-white
              "
            >
              {hiddenColumnsCount}
            </span>

            {/* INDICATOR DOT */}
            <span
              className="
                absolute -right-1 -top-1
                h-3 w-3
                rounded-full
                border-2 border-white
                bg-amber-500
                animate-pulse
                dark:border-slate-900
              "
            />
          </>
        )}
      </button>

      {/* DROPDOWN */}
      {open && (
        <div
          className="
            absolute right-0 z-50 mt-2
            w-64 overflow-hidden
            rounded-2xl
            border border-slate-200 dark:border-slate-700
            bg-white dark:bg-slate-900
            shadow-xl
          "
        >
          {/* HEADER */}
          <div
            className="
              flex items-center justify-between
              border-b border-slate-200 dark:border-slate-700
              px-4 py-3
            "
          >
            <div>
              <h3
                className="
                  text-sm font-semibold
                  text-slate-900 dark:text-white
                "
              >
                Manage Columns
              </h3>

              <p
                className="
                  mt-0.5 text-xs
                  text-slate-500 dark:text-slate-400
                "
              >
                Customize visible table columns
              </p>
            </div>

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="
                rounded-lg p-1.5
                hover:bg-slate-100
                dark:hover:bg-slate-800
              "
            >
              <X size={16} className="text-slate-500" />
            </button>
          </div>

          {/* ACTIONS */}
          <div
            className="
              flex items-center gap-2
              border-b border-slate-200 dark:border-slate-700
              p-3
            "
          >
            <button
              type="button"
              onClick={handleSelectAll}
              className="
                flex-1 rounded-xl
                bg-slate-100 dark:bg-slate-800
                px-3 py-2
                text-xs font-medium
                text-slate-700 dark:text-slate-200
                transition-colors
                hover:bg-slate-200
                dark:hover:bg-slate-700
              "
            >
              Select All
            </button>

            <button
              type="button"
              onClick={handleReset}
              className="
                flex items-center justify-center gap-1
                rounded-xl
                bg-slate-100 dark:bg-slate-800
                px-3 py-2
                text-xs font-medium
                text-slate-700 dark:text-slate-200
                transition-colors
                hover:bg-slate-200
                dark:hover:bg-slate-700
              "
            >
              <RotateCcw size={12} />

              <span>Reset</span>
            </button>
          </div>

          {/* COLUMN LIST */}
          <div className="max-h-80 space-y-1 overflow-auto p-2">
            {columns.map((col) => {
              const checked = visibleColumns.includes(col.key);

              const disabled = col.hideable === false;

              return (
                <button
                  key={col.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => !disabled && toggleColumn(col.key)}
                  className={`
          flex w-full items-center justify-between
          rounded-xl px-3 py-2 transition-colors

          ${
            disabled
              ? "cursor-not-allowed opacity-50"
              : "hover:bg-slate-100 dark:hover:bg-slate-800"
          }
        `}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {typeof col.label === "function" ? col.key : col.label}
                    </span>
                  </div>

                  <div
                    className={`
            flex h-5 w-5 items-center justify-center
            rounded-md border transition-all

            ${
              checked
                ? "border-teal-600 bg-teal-600 text-white"
                : "border-slate-300 dark:border-slate-600"
            }
          `}
                  >
                    {checked && <Check size={12} />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnVisibilityDropdown;
